import React from 'react';
import { Link } from 'react-router-dom';
import './Specialist.css';

const Specialist = () => {
  const services = [
    {
      id: 1,
      title: "Residential Solar Solutions",
      icon: "fas fa-home",
      description: "Complete solar power systems for homes and residential properties across Botswana.",
      features: [
        "Solar panel installation for homes",
        "Battery backup systems",
        "Grid-tied and off-grid solutions",
        "Energy consumption analysis",
        "Government rebate assistance"
      ],
      bgColor: "#f0f9ff",
      iconColor: "#3498db"
    },
    {
      id: 2,
      title: "Commercial Solar Systems",
      icon: "fas fa-building",
      description: "Large-scale solar solutions for businesses, offices, and commercial establishments.",
      features: [
        "Commercial rooftop installations",
        "Solar carports and shade structures",
        "Energy management systems",
        "Peak shaving solutions",
        "Return on investment analysis"
      ],
      bgColor: "#f8fff0",
      iconColor: "#25D366"
    },
    {
      id: 3,
      title: "Industrial Solar Power",
      icon: "fas fa-industry",
      description: "High-capacity solar solutions for factories, manufacturing plants, and industrial facilities.",
      features: [
        "Megawatt-scale solar farms",
        "Industrial load management",
        "Power quality improvement",
        "Production line solar integration",
        "24/7 monitoring and maintenance"
      ],
      bgColor: "#fff8f0",
      iconColor: "#ff6b00"
    },
    {
      id: 4,
      title: "Solar Water Heating",
      icon: "fas fa-water",
      description: "Efficient solar water heating systems for residential and commercial applications.",
      features: [
        "Domestic hot water systems",
        "Swimming pool heating",
        "Commercial hot water solutions",
        "Thermal storage systems",
        "Backup electric heating"
      ],
      bgColor: "#f0f8ff",
      iconColor: "#2980b9"
    },
    {
      id: 5,
      title: "Solar Pumping Systems",
      icon: "fas fa-tint",
      description: "Reliable solar-powered water pumping for agriculture, livestock, and community water supply.",
      features: [
        "Agricultural irrigation systems",
        "Livestock watering solutions",
        "Borehole and well pumping",
        "Water treatment plants",
        "Remote area water supply"
      ],
      bgColor: "#f0fff8",
      iconColor: "#06d6a0"
    },
    {
      id: 6,
      title: "Solar Street Lighting",
      icon: "fas fa-lightbulb",
      description: "Sustainable solar street lighting solutions for municipalities, roads, and public spaces.",
      features: [
        "LED solar street lights",
        "Public park lighting",
        "Security lighting systems",
        "Automatic dusk-to-dawn operation",
        "Remote monitoring and control"
      ],
      bgColor: "#fff8f8",
      iconColor: "#e74c3c"
    }
  ];

  const processSteps = [
    {
      step: 1,
      title: "Consultation & Assessment",
      description: "Free site evaluation and energy needs analysis"
    },
    {
      step: 2,
      title: "Custom Design",
      description: "Tailored solar solution design for your specific requirements"
    },
    {
      step: 3,
      title: "Installation",
      description: "Professional installation by certified technicians"
    },
    {
      step: 4,
      title: "Commissioning",
      description: "System testing, optimization, and handover"
    },
    {
      step: 5,
      title: "Maintenance & Support",
      description: "Ongoing monitoring, maintenance, and customer support"
    }
  ];

  return (
    <div className="specialist-page">
      {/* Hero Section */}
      {/* Hero Section - Matches Contact Page */}
      <section className="specialist-hero">
        <div className="container">
          <div className="hero-content">
            <h1>Solar Energy Specialist Services</h1>
            <p className="hero-subtitle">
              Comprehensive solar solutions tailored for Botswana's unique climate and energy needs. 
              From residential installations to industrial solar farms, we deliver excellence at every scale.
            </p>
            
            <div className="specialist-hero-stats">
              <div className="stat-item-1">
                <span className="stat-number">6</span>
                <span className="stat-label">Service Categories</span>
              </div>
              <div className="stat-item-1">
                <span className="stat-number">500+</span>
                <span className="stat-label">Projects Completed</span>
              </div>
              <div className="stat-item-1">
                <span className="stat-number">100%</span>
                <span className="stat-label">Customer Satisfaction</span>
              </div>
              <div className="stat-item-1">
                <span className="stat-number">24/7</span>
                <span className="stat-label">Technical Support</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="services-section py-5">
        <div className="container">
          <div className="section-header text-center mb-5">
            <h2 className="display-5 fw-bold text-dark mb-3">Our Specialist Services</h2>
            <p className="lead text-muted">
              We provide end-to-end solar energy solutions for every sector in Botswana
            </p>
          </div>

          <div className="row g-4">
            {services.map((service) => (
              <div key={service.id} className="col-lg-4 col-md-6">
                <div 
                  className="service-card h-100 p-4 rounded-4 shadow-sm"
                  style={{ backgroundColor: service.bgColor }}
                >
                  <div className="service-icon mb-4">
                    <i 
                      className={`${service.icon} fa-3x`}
                      style={{ color: service.iconColor }}
                    ></i>
                  </div>
                  <h3 className="h4 fw-bold text-dark mb-3">{service.title}</h3>
                  <p className="text-muted mb-4">{service.description}</p>
                  <ul className="service-features list-unstyled">
                    {service.features.map((feature, index) => (
                      <li key={index} className="mb-2">
                        <i className="fas fa-check-circle text-success me-2"></i>
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <Link to="/about" className="btn btn-outline-primary mt-3">
                    Learn More <i className="fas fa-arrow-right ms-2"></i>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="process-section py-5 bg-light">
        <div className="container">
          <div className="section-header text-center mb-5">
            <h2 className="display-5 fw-bold text-dark mb-3">Our 5-Step Process</h2>
            <p className="lead text-muted">
              A streamlined approach to delivering exceptional solar solutions
            </p>
          </div>

          <div className="process-timeline">
            {processSteps.map((step) => (
              <div key={step.step} className="process-step">
                <div className="step-number">
                  <span>{step.step}</span>
                </div>
                <div className="step-content">
                  <h4 className="h5 fw-bold text-dark mb-2">{step.title}</h4>
                  <p className="text-muted mb-0">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section py-5">
        <div className="container">
          <div className="cta-card bg-primary text-white rounded-4 p-5 text-center">
            <h2 className="display-6 fw-bold mb-4">Ready to Go Solar?</h2>
            <p className="lead mb-4 opacity-90">
              Get a free consultation and custom quote for your solar energy project
            </p>
            <div className="cta-buttons">
              <Link to="/contact" className="btn btn-light btn-lg px-5 me-3">
                <i className="fas fa-calendar-alt me-2"></i> Schedule Consultation
              </Link>
              <a href="tel:0861111497" className="btn btn-outline-light btn-lg px-5">
                <i className="fas fa-phone-alt me-2"></i> Call 0861 111 497
              </a>
            </div>
          </div>
        </div>
      </section>


    </div>
  );
};

export default Specialist;